export type BillingInvoiceDueDateInput = {
  status?: unknown;
  invoiceNumber?: unknown;
  invoiceDate?: unknown;
  invoiceFiscalVerified?: unknown;
  paymentTermBasis?: unknown;
  paymentTerms?: unknown;
  balanceAmount?: unknown;
  collectionBalanceCents?: unknown;
};

function dateOnly(value: unknown): Date | null {
  if (value == null || value === "") return null;
  let year: number;
  let month: number;
  let day: number;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    year = value.getUTCFullYear();
    month = value.getUTCMonth() + 1;
    day = value.getUTCDate();
  } else if (typeof value === "string") {
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/);
    if (!match) return null;
    year = Number(match[1]);
    month = Number(match[2]);
    day = Number(match[3]);
  } else {
    return null;
  }
  const result = new Date(Date.UTC(year, month - 1, day));
  return result.getUTCFullYear() === year && result.getUTCMonth() === month - 1 && result.getUTCDate() === day
    ? result
    : null;
}

function paymentTermsDays(value: unknown): number | null {
  if (typeof value === "number") return Number.isSafeInteger(value) && value >= 0 ? value : null;
  if (typeof value !== "string" || !/^\d+$/.test(value.trim())) return null;
  const parsed = Number(value.trim());
  return Number.isSafeInteger(parsed) ? parsed : null;
}

function moneyCents(value: unknown): number | null {
  if (typeof value !== "number" && typeof value !== "string") return null;
  const normalized = String(value).trim();
  if (!/^-?\d+(?:\.\d{1,2})?$/.test(normalized)) return null;
  const amount = Number(normalized);
  return Number.isFinite(amount) ? Math.round(amount * 100) : null;
}

export function hasVerifiedBillingInvoiceIdentity(input: BillingInvoiceDueDateInput): boolean {
  return typeof input.invoiceNumber === "string"
    && Boolean(input.invoiceNumber.trim())
    && dateOnly(input.invoiceDate) !== null
    && input.invoiceFiscalVerified === true;
}

export function getBillingInvoiceDueDate(input: BillingInvoiceDueDateInput): Date | null {
  if (!hasVerifiedBillingInvoiceIdentity(input) || input.paymentTermBasis !== "invoice_date") return null;
  const invoiceDate = dateOnly(input.invoiceDate);
  const days = paymentTermsDays(input.paymentTerms);
  if (!invoiceDate || days === null) return null;
  invoiceDate.setUTCDate(invoiceDate.getUTCDate() + days);
  return invoiceDate;
}

export function getVerifiedInvoiceBalanceCents(input: BillingInvoiceDueDateInput): number | null {
  if (input.balanceAmount !== undefined && input.balanceAmount !== null) return moneyCents(input.balanceAmount);
  if (typeof input.collectionBalanceCents === "number" && Number.isSafeInteger(input.collectionBalanceCents)) {
    return input.collectionBalanceCents;
  }
  return null;
}

export function isBillingInvoiceOverdue(input: BillingInvoiceDueDateInput, today: Date = new Date()): boolean {
  if (!["invoiced", "sent_to_client", "pending_collection"].includes(String(input.status ?? ""))) return false;
  if (!hasVerifiedBillingInvoiceIdentity(input)) return false;
  const balanceCents = getVerifiedInvoiceBalanceCents(input);
  if (balanceCents === null || balanceCents <= 0) return false;
  const dueDate = getBillingInvoiceDueDate(input);
  const currentDate = dateOnly(today);
  return Boolean(dueDate && currentDate && dueDate < currentDate);
}
